import React, { useState, useRef, useEffect } from 'react';
import HistoryIcon from "@mui/icons-material/History";
import "../../styles/HistoryTool.css";

export const HistoryToolConfig = {
  name: "HISTORY",
  tool: "History",
  icon: <HistoryIcon />,
};

const BASE_URL = "http://127.0.0.1:8000"; 

export const openPatientHistory = (patientId) => {
  console.log('Received Patient ID in utility:', patientId);

  // Clean up any stale instances left behind in the DOM
  const existingRoot = document.getElementById('history-modal-root');
  if (existingRoot) {
    existingRoot.remove();
  }

  // Create a clean root element
  const modalRoot = document.createElement('div');
  modalRoot.id = 'history-modal-root';
  document.body.appendChild(modalRoot);

  // Using dynamic import safely
  import('react-dom/client').then(({ createRoot }) => {
    const root = createRoot(modalRoot);
    
    // Explicitly convert to string here to guarantee prop types match string states downstream
    const targetId = patientId !== undefined && patientId !== null ? String(patientId) : '';

    root.render(
      <PatientHistoryModal 
        patientId={targetId}
        onClose={() => {
          root.unmount();
          modalRoot.remove();
        }}
      />
    );
  }).catch(err => {
    console.error("Failed to load React-DOM dynamically:", err);
  });
};

// React Component
const PatientHistoryModal = ({ patientId: incomingPatientId, onClose }) => {
  // Convert incoming ID to string safely in case it is a number
  const [patientId, setPatientId] = useState(incomingPatientId || '');
  const [patientName, setPatientName] = useState('');
  const [historyText, setHistoryText] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [lookupStatus, setLookupStatus] = useState('');
  const [lookupStatusColor, setLookupStatusColor] = useState('#6b7280');
  const [patientFound, setPatientFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingHistory, setExistingHistory] = useState(null);
  
  const fileInputRef = useRef(null);
  const performLookup = async (idToSearch) => {
    console.log("🔍 API Lookup executing for ID:", idToSearch);
    
    if (!idToSearch || !String(idToSearch).trim()) {
      setLookupStatus("⚠ Enter the Patient ID ");
      setLookupStatusColor("#f59e0b");
      return;
    }

    setLookupStatus("Searching...");
    setLookupStatusColor("#6b7280");
    setPatientName("");
    setPatientFound(false);

    try {
      const res = await fetch(
        `${BASE_URL}/api/patient-lookup/?patient_id=${encodeURIComponent(idToSearch.trim())}`
      );
      const data = await res.json();

      if (res.ok && data.found) {
        setPatientName(data.patient_name);
        setLookupStatus("✓ Patient found");
        setLookupStatusColor("#1D9E75");
        setPatientFound(true);
        
        // Fetch history immediately
        console.log("🔍 Fetching history for verified ID:", idToSearch);
        const historyRes = await fetch(
          `${BASE_URL}/api/patient-history/?patient_id=${encodeURIComponent(idToSearch.trim())}`
        );
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          if (historyData && historyData.length > 0) {
            setExistingHistory(historyData[0]);
            setHistoryText(historyData[0].history_text || "");
            if (historyData[0].attachment) {
              setAttachmentPreview(`${BASE_URL}${historyData[0].attachment}`);
            }
          }
        }
      } else {
        setLookupStatus(`✗ Patient ID "${idToSearch}" not found`);
        setLookupStatusColor("#ef4444");
      }
    } catch (err) {
      console.error("❌ API Execution Failed:", err);
      setLookupStatus("⚠ Not able to connect to the server");
      setLookupStatusColor("#ef4444");
    }
  };
  useEffect(() => {
    console.log("🧩 Modal component successfully mounted with ID:", incomingPatientId);
    if (incomingPatientId) {
      performLookup(incomingPatientId);
    }
  }, []);
  const handleManualSearch = () => {
    performLookup(patientId);
  };
  const handleLookupPatient = async (targetId = patientId) => {
    console.log("lookup called with ID:", targetId);
    
    // Ensure we are working with a string and trim it safely
    const searchId = String(targetId || '').trim();

    if (!searchId) {
      setLookupStatus("⚠ Enter the Patient ID ");
      setLookupStatusColor("#f59e0b");
      return;
    }

    setLookupStatus("Searching...");
    setLookupStatusColor("#6b7280");
    setPatientName("");
    setPatientFound(false);

    try {
      const res = await fetch(
        `${BASE_URL}/api/patient-lookup/?patient_id=${encodeURIComponent(searchId)}`
      );
      const data = await res.json();

      if (res.ok && data.found) {
        setPatientName(data.patient_name);
        setLookupStatus("✓ Patient found");
        setLookupStatusColor("#1D9E75");
        setPatientFound(true);
        fetchPatientHistory(searchId); // Fetch history using the same verified ID
      } else {
        setLookupStatus(`✗ Patient ID "${searchId}" not found`);
        setLookupStatusColor("#ef4444");
      }
    } catch (err) {
      console.error("Lookup error:", err);
      setLookupStatus("⚠ Not able to connect to the server");
      setLookupStatusColor("#ef4444");
    }
  };


  const handlePatientIdKeyDown = (e) => {
    if (e.key === "Enter") {
      performLookup(patientId);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const fetchPatientHistory = async (patientId) => {
    console.log("fetchPatientHistory called", patientId);
    try {
      const res = await fetch(
        `${BASE_URL}/api/patient-history/?patient_id=${encodeURIComponent(patientId)}`
      );
  
      if (res.ok) {
        const data = await res.json();
  
        if (data.length > 0) {
          // Latest history
          setExistingHistory(data[0]);
  
          // Show in textarea
          setHistoryText(data[0].history_text || "");
  
          // Optional attachment preview
          if (data[0].attachment) {
            setAttachmentPreview(`${BASE_URL}${data[0].attachment}`);
          }
        } else {
          setExistingHistory(null);
          setHistoryText("");
          setAttachmentPreview(null);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };
 
  // Reset state when patient ID changes
  const handlePatientIdChange = (e) => {
    setPatientId(e.target.value);
    setPatientFound(false);
    setPatientName("");
    setLookupStatus("");
    setExistingHistory(null);
    setHistoryText("");
    setAttachment(null);
    setAttachmentPreview(null);
    setAttachmentType(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  // Handle file attachment - Accept ALL file types including DICOM
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAttachment(file);
    const fileType = file.type;
    const fileName = file.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();
    const isDicom = fileExtension === 'dcm' ||  fileExtension === 'dic' || fileType === 'application/dicom' || fileType === '' || (fileExtension === '' && fileName.includes('.')); 
    const isImage = fileType.startsWith('image/') || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png' || fileExtension === 'gif' || fileExtension === 'webp' || fileExtension === 'bmp';
    const isPdf = fileType === 'application/pdf' || fileExtension === 'pdf';
    if (isImage && !isDicom) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachmentPreview(e.target.result);
        setAttachmentType('image');
      };
      reader.readAsDataURL(file);
    } else if (isPdf) {
      setAttachmentPreview(null);
      setAttachmentType('pdf');
    } else if (isDicom) {
      setAttachmentPreview(null);
      setAttachmentType('dicom');
    } else {
      // Other file types (text, etc.)
      setAttachmentPreview(null);
      setAttachmentType('other');
    }
  };

  // Remove attachment
  const handleRemoveAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    setAttachmentType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Get file icon/display based on type
  const renderAttachmentPreview = () => {
    if (!attachment) return null;
    const fileName = attachment.name;
    const fileSize = (attachment.size / 1024 / 1024).toFixed(2); // Size in MB
    if (attachmentType === 'image' && attachmentPreview) {
      return (
        <div className="history-modal__attachment-preview">
          <img src={attachmentPreview} alt="Preview" className="history-modal__preview-image" />
          <div className="history-modal__attachment-info">
            <span className="history-modal__attachment-name">{fileName}</span>
            <span className="history-modal__attachment-size">({fileSize} MB)</span>
            <button onClick={handleRemoveAttachment} className="history-modal__remove-attachment">
              Remove
            </button>
          </div>
        </div>
      );
    } else if (attachmentType === 'pdf') {
      return (
        <div className="history-modal__attachment-preview">
          <div className="history-modal__pdf-icon">📄 PDF</div>
          <div className="history-modal__attachment-info">
            <span className="history-modal__attachment-name">{fileName}</span>
            <span className="history-modal__attachment-size">({fileSize} MB)</span>
            <button onClick={handleRemoveAttachment} className="history-modal__remove-attachment">
              Remove
            </button>
          </div>
        </div>
      );
    } else if (attachmentType === 'dicom') {
      return (
        <div className="history-modal__attachment-preview">
          <div className="history-modal__dicom-icon">🩻 DICOM</div>
          <div className="history-modal__attachment-info">
            <span className="history-modal__attachment-name">{fileName || 'DICOM file'}</span>
            <span className="history-modal__attachment-size">({fileSize} MB)</span>
            <p className="history-modal__dicom-note">
              Note: DICOM image will be stored and can be viewed in PACS viewer
            </p>
            <button onClick={handleRemoveAttachment} className="history-modal__remove-attachment">
              Remove
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="history-modal__attachment-preview">
          <div className="history-modal__file-icon">📎 File</div>
          <div className="history-modal__attachment-info">
            <span className="history-modal__attachment-name">{fileName}</span>
            <span className="history-modal__attachment-size">({fileSize} MB)</span>
            <button onClick={handleRemoveAttachment} className="history-modal__remove-attachment">
              Remove
            </button>
          </div>
        </div>
      );
    }
  };

  // Save History
  const handleSaveHistory = async () => {
    if (!patientFound) return;

    if (!historyText.trim()) {
      alert("Write some history of patient ");
      return;
    }

    const formData = new FormData();
    formData.append("patient_id_input", patientId);
    formData.append("history_text", historyText);

    if (attachment) {
      formData.append("attachment", attachment);
    }

    setIsSaving(true);

    try {
      const res = await fetch(`${BASE_URL}/api/patient-history/`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        alert(
          existingHistory
            ? "✓ History updated successfully."
            : "✓ History saved successfully."
        );
        
        setAttachment(null);
        setAttachmentPreview(null);
        setAttachmentType(null);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        
        fetchPatientHistory(patientId);
        setIsSaving(false);
        onClose();
      } else {
        const errMsg = JSON.stringify(data);
        alert(`Error saving: ${errMsg}`);
        setIsSaving(false);
      }
    } catch (err) {
      alert("Server error.");
      setIsSaving(false);
    }
  };

  return (
    <div className="history-overlay" onClick={handleOverlayClick}>
      <div className="history-modal">
        <div className="history-modal__header">
          <h2 className="history-modal__title">Patient History </h2>
  
          <button
            id="closeHistoryModal"
            className="history-modal__close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
  
        {/* Patient ID */}
        <div className="history-modal__field">
          <label className="history-modal__label history-modal__label--required">
            Patient ID
          </label>
  
          <div className="history-modal__id-row">
            <input
              type="text"
              id="historyPatientId"
              placeholder="e.g. P001"
              className="history-modal__input"
              value={patientId}
              onChange={handlePatientIdChange}
              onKeyDown={handlePatientIdKeyDown}
            />
  
            <button
              id="lookupPatientBtn"
              className="history-modal__lookup-btn"
              onClick={handleLookupPatient}
            >
              Search
            </button>
          </div>
  
          <p
            id="lookupStatus"
            className="history-modal__status"
            style={{ color: lookupStatusColor }}
          >
            {lookupStatus}
          </p>
        </div>
  
        {/* Patient Name */}
        <div className="history-modal__field">
          <label className="history-modal__label">
            Patient Name
          </label>
  
          <input
            type="text"
            id="historyPatientName"
            placeholder="Auto-filled after search"
            readOnly
            className="history-modal__input--readonly"
            value={patientName}
          />
        </div>
  
        {/* History */}
        <div className="history-modal__field">
          <label className="history-modal__label history-modal__label--required">
            History
          </label>
  
          <textarea
            id="historyText"
            placeholder="Patient history write here..."
            className="history-modal__textarea"
            value={historyText}
            onChange={(e) => setHistoryText(e.target.value)}
          />
  
          {existingHistory && (
            <div
              style={{
                marginTop: "8px",
                color: "#1D9E75",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              ✓ Existing history loaded
            </div>
          )}
        </div>
  
        {/* Existing Attachment */}
        {existingHistory?.attachment && !attachment && (
          <div className="history-modal__field">
            <label className="history-modal__label">
              Existing Attachment
            </label>
  
            <a
              href={`${BASE_URL}${existingHistory.attachment}`}
              target="_blank"
              rel="noopener noreferrer"
              className="history-modal__attachment-link"
            >
              📎 View Existing Attachment
            </a>
          </div>
        )}
  
        {/* Upload Attachment */}
        <div className="history-modal__field history-modal__field--attachment">
          <label className="history-modal__label">
            {existingHistory
              ? "Replace Attachment (Optional)"
              : "Attachment (Optional)"}
          </label>
  
          <input
            type="file"
            id="historyAttachment"
            className="history-modal__file-input"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="*/*"
          />
  
          <p className="history-modal__file-hint">
            {existingHistory
              ? "Leave empty to keep the existing attachment."
              : "Supported: Images, PDF, DICOM (.dcm/.dic) and all other file types."}
          </p>
        </div>
  
        {/* New Attachment Preview */}
        {attachment && renderAttachmentPreview()}
  
        {/* Save Button */}
        <button
          id="saveHistoryBtn"
          disabled={!patientFound || !historyText.trim() || isSaving}
          className={`history-modal__save-btn ${
            patientFound && historyText.trim() && !isSaving
              ? "history-modal__save-btn--enabled"
              : "history-modal__save-btn--disabled"
          }`}
          onClick={handleSaveHistory}
        >
          {isSaving
            ? existingHistory
              ? "Updating..."
              : "Saving..."
            : existingHistory
            ? "Update History"
            : "Add History"}
        </button>
      </div>
    </div>
  );
};